@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.yard

import androidx.compose.runtime.*
import kotlinx.coroutines.delay
import org.example.project.dashboard.LocalScreenRefreshVersion
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlin.math.*
import kotlinx.coroutines.launch
import org.example.project.dashboard.DashboardTheme
import org.example.project.dashboard.PopupEngineCard
import org.example.project.enginekartei.KarteiApi
import org.example.project.service.InfrastrukturApi
import org.example.project.service.YardApi
import org.example.project.utils.All
import org.example.project.service.ZustandManager
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.AppDropdown
import org.example.project.settings.applyAppButtonActiveChrome
import org.example.project.settings.applyAppButtonChrome
import org.example.project.settings.applyAppButtonHoverChrome


/* ================= CONFIG ================= */

private const val GRID_SIZE = 200
private const val TILE_SIZE = 24

private const val HALLE = "HALLE"
private const val WEG = "WEG"
private const val HOF = "HOF"
private const val PARKEN = "PARKEN"
private const val VERWALTUNG = "VERWALTUNG"
private const val PAPIER = "PAPIER"
private const val TOILETTE = "TOILETTE"
private const val KANTINE = "KANTINE"
private const val EINFAHRT = "EINFAHRT"
private const val AUSFAHRT = "AUSFAHRT"
private const val TOR = "TOR"
private const val PARKPLATZ = "PARKPLATZ"

private const val LABEL_WERKSGELAENDE = "WERKSGELAENDE"
private const val LABEL_PAPIERAUSGABE = "PAPIERAUSGABE"
private const val LABEL_PAPIERANNAHME = "PAPIERANNAHME"
private const val LABEL_PARKBEREICH = "PARKBEREICH"
private const val LABEL_HALLE = "HALLE"
private const val LABEL_TOILETTEN = "TOILETTEN"
private const val LABEL_KANTINE = "KANTINE"
private const val LABEL_EINFAHRT = "EINFAHRT"
private const val LABEL_AUSFAHRT = "AUSFAHRT"

/* ================= DATA ================= */

class Block(
    val tiles: Map<Pair<Int, Int>, String>
)

class Tor(
    val id: Int,
    val x: Int,
    val y: Int
)

class Parkplatz(
    val id: Int,
    val x: Int,
    val y: Int
)

class Outline(
    val cells: Set<Pair<Int, Int>>
)

class AreaLabel(
    val id: Int,
    val name: String,
    val cells: Set<Pair<Int, Int>>,
    val type: String = "",
    val reference: String = ""
)

private fun areaLabelName(type: String, reference: String = ""): String =
    when (type) {
        LABEL_WERKSGELAENDE -> "Werksgelände"
        LABEL_PAPIERAUSGABE -> "Papierausgabe"
        LABEL_PAPIERANNAHME -> "Papierannahme"
        LABEL_PARKBEREICH -> "Parkbereich"
        LABEL_HALLE -> "Halle ${reference.trim()}".trim()
        LABEL_TOILETTEN -> "Toiletten"
        LABEL_KANTINE -> "Kantine"
        LABEL_EINFAHRT -> "Einfahrt"
        LABEL_AUSFAHRT -> "Ausfahrt"
        else -> ""
    }

private fun hallenIds(standort: All?): List<String> {
    val from = standort?.halleVon.orEmpty().trim()
    val to = standort?.halleBis.orEmpty().trim()
    val first = from.toIntOrNull()
    val last = to.toIntOrNull()
    return if (first != null && last != null) {
        val width = maxOf(from.length, to.length)
        (minOf(first, last)..maxOf(first, last)).map { it.toString().padStart(width, '0') }
    } else {
        listOf(from, to).filter(String::isNotBlank).distinct()
    }
}

private fun parseCell(raw: String): Pair<Int, Int>? {
    val parts = raw.split(",")
    if (parts.size != 2) return null
    val x = parts[0].trim().toIntOrNull() ?: return null
    val y = parts[1].trim().toIntOrNull() ?: return null
    return x to y
}


fun torColor(zustand: Int?): CSSColorValue =
    when (zustand) {
        ZustandManager.RESERVIERT,
        ZustandManager.TOR_ZUGEWIESEN -> Color("#FFA726") // orange

        ZustandManager.AM_TOR,
        ZustandManager.PIN_ABFRAGE,
        ZustandManager.IN_VERLADUNG -> Color("#EF5350")   // rot

        ZustandManager.ANGELEGT,
        null -> Color("#6FBF73") // grün = frei / default

        else -> Color("#9E9E9E") //
    }

/* ================= SCREEN ================= */

@Composable
fun StandortLayoutVerwaltung(){



    var standorte by remember { mutableStateOf<List<All>>(emptyList()) }
    var selectedStandortId by remember { mutableStateOf<String?>(null) }
    var echteTore by remember { mutableStateOf<List<All>>(emptyList()) }
    var echteParkplaetze by remember { mutableStateOf<List<All>>(emptyList()) }

    /* ---- global ---- */
    var editMode by remember { mutableStateOf(true) }
    var paintType by remember { mutableStateOf(HALLE) }

    /* ---- zoom ---- */
    var zoom by remember { mutableStateOf(1.0) }
    val minzoom = 0.5
    val maxzoom= 2.5

    /* ---- data ---- */
    val tiles = remember { mutableStateMapOf<Pair<Int, Int>, String>() }
    val tore = remember { mutableStateListOf<Tor>() }
    val parkplaetze = remember { mutableStateListOf<Parkplatz>() }
    val outlines = remember { mutableStateListOf<Outline>() }
    val labels = remember { mutableStateListOf<AreaLabel>() }

    var labelCounter by remember { mutableStateOf(1) }

    /* ---- painting ---- */
    var isPainting by remember { mutableStateOf(false) }

    /* ---- selection ---- */
    var rightDown by remember { mutableStateOf(false) }
    var dragStart by remember { mutableStateOf<Pair<Int, Int>?>(null) }
    var dragCurrent by remember { mutableStateOf<Pair<Int, Int>?>(null) }
    var selection by remember { mutableStateOf<Set<Pair<Int, Int>>>(emptySet()) }

    /* ---- menu ---- */
    var menuVisible by remember { mutableStateOf(false) }
    var menuPos by remember { mutableStateOf(0 to 0) }
    var labelMenuOpen by remember { mutableStateOf(false) }
    var hallMenuOpen by remember { mutableStateOf(false) }
    var labelDialogOpen by remember { mutableStateOf(false) }
    var labelDialogType by remember { mutableStateOf(LABEL_WERKSGELAENDE) }
    var labelDialogHalle by remember { mutableStateOf("") }
    var labelDialogCells by remember { mutableStateOf<Set<Pair<Int, Int>>>(emptySet()) }
    var labelDialogEditId by remember { mutableStateOf<Int?>(null) }

    /* ---- clipboard ---- */
    var clipboard by remember { mutableStateOf<Block?>(null) }
    var ghostActive by remember { mutableStateOf(false) }
    var ghostPos by remember { mutableStateOf<Pair<Int, Int>?>(null) }


    val scope = rememberCoroutineScope()
    val refreshVersion = LocalScreenRefreshVersion.current


    LaunchedEffect(refreshVersion) {
        val loaded = KarteiApi.loadStandorte()
        standorte = loaded

        if (selectedStandortId == null && loaded.isNotEmpty()) {
            selectedStandortId = loaded.first().id
        }
    }



    LaunchedEffect(selectedStandortId) {

        val standortId = selectedStandortId ?: return@LaunchedEffect

        // 🔒 HARTER RESET – NUR STATE, KEINE LOGIKÄNDERUNG
        tiles.clear()
        tore.clear()
        parkplaetze.clear()
        outlines.clear()
        labels.clear()
        labelCounter = 1
        zoom = 1.0

        selection = emptySet()
        ghostActive = false
        ghostPos = null
        menuVisible = false
        isPainting = false

        YardApi.loadStandortLayout(standortId)?.let { all ->

            all.standortTiles.forEach { (k, v) ->
                parseCell(k)?.let { (x, y) ->
                    if (v.isNotBlank()) tiles[x to y] = v
                }
            }

            all.standortTore.forEach {
                val id = (it["id"] ?: 0).takeIf { value -> value > 0 } ?: return@forEach
                tore.add(Tor(id, it["x"] ?: 0, it["y"] ?: 0))
            }
            all.standortParkplaetze.forEach {
                val id = (it["id"] ?: 0).takeIf { value -> value > 0 } ?: return@forEach
                parkplaetze.add(Parkplatz(id, it["x"] ?: 0, it["y"] ?: 0))
            }
            all.standortOutlines.forEach { list ->
                val cells = list.mapNotNull(::parseCell).toSet()
                if (cells.isNotEmpty()) outlines.add(Outline(cells))
            }

            all.standortLabels.forEach { map ->
                val id = map["id"]?.toIntOrNull() ?: labelCounter++
                val name = map["name"] ?: "Bereich"

                val cells = map["cells"]
                    ?.split(";")
                    ?.mapNotNull(::parseCell)
                    ?.toSet()
                    ?: emptySet()

                if (cells.isNotEmpty()) {
                    labels.add(
                        AreaLabel(
                            id = id,
                            name = name,
                            cells = cells,
                            type = map["type"].orEmpty(),
                            reference = map["reference"].orEmpty()
                        )
                    )
                }
            }

            labelCounter = (labels.maxOfOrNull { it.id } ?: 0) + 1
            zoom = all.standortZoom.takeIf { it > 0.0 } ?: 1.0
        }

        while (true) {
            val loadedTore = runCatching {
                InfrastrukturApi.loadTore()
                    .filter { it.standort == standortId && (it.tor ?: 0) > 0 }
                    .sortedBy { it.tor ?: 0 }
            }.getOrDefault(emptyList())
            echteTore = loadedTore
            val gueltigeTorIds = loadedTore.mapNotNull { it.tor }.toSet()
            tore.removeAll { it.id !in gueltigeTorIds }
            val loadedParkplaetze = runCatching {
                InfrastrukturApi.loadParkplaetze()
                    .filter { it.standort == standortId && it.id.toIntOrNull() != null }
                    .sortedBy { it.id.toIntOrNull() ?: 0 }
            }.getOrDefault(emptyList())
            echteParkplaetze = loadedParkplaetze
            val gueltigeParkplatzIds = loadedParkplaetze.mapNotNull { it.id.toIntOrNull() }.toSet()
            parkplaetze.removeAll { it.id !in gueltigeParkplatzIds }
            delay(3000)
        }
    }


    /* ================= HELPERS ================= */

    fun tileColor(type: String?): CSSColorValue =
        when (type) {
            HALLE -> Color("#d9dee5")
            WEG -> Color("#c62828")
            HOF -> Color("#2f343a")
            PARKEN -> Color("#242a31")
            VERWALTUNG -> Color("#1976d2")
            PAPIER -> Color("#f28c28")
            TOILETTE -> Color("#00a6a6")
            KANTINE -> Color("#8e5bb7")
            EINFAHRT -> Color("#2e7d32")
            AUSFAHRT -> Color("#ad1457")
            else -> Color.transparent
        }

    fun nextFreiesTorId(): Int? {
        val belegteIds = tore.map { it.id }.toSet()
        return echteTore
            .mapNotNull { it.tor }
            .sorted()
            .firstOrNull { it !in belegteIds }
    }

    fun nextFreierParkplatzId(): Int? {
        val belegteIds = parkplaetze.map { it.id }.toSet()
        return echteParkplaetze
            .mapNotNull { it.id.toIntOrNull() }
            .sorted()
            .firstOrNull { it !in belegteIds }
    }

    fun addTor(x: Int, y: Int) {
        if (tore.none { it.x == x && it.y == y }) {
            val torId = nextFreiesTorId() ?: return
            tore.add(Tor(torId, x, y))
        }
    }

    fun addParkplatz(x: Int, y: Int) {
        if (parkplaetze.none { it.x == x && it.y == y }) {
            val parkplatzId = nextFreierParkplatzId() ?: return
            parkplaetze.add(Parkplatz(parkplatzId, x, y))
        }
    }

    fun removeTor(x: Int, y: Int) {
        tore.removeAll { it.x == x && it.y == y }
    }

    fun removeParkplatz(x: Int, y: Int) {
        parkplaetze.removeAll { it.x == x && it.y == y }
    }

    /* ================= UI ================= */

    Div {

        /* ===== TOOLBAR ===== */

        Div({
            style {
                display(DisplayStyle.Flex)
                gap(8.px)
                marginBottom(8.px)
                alignItems(AlignItems.Center)
            }
        }) {
            AppDropdown(
                options = standorte.map { s ->
                    All(key = s.id, label = s.standort.ifBlank { "Standort ${s.id}" })
                },
                selectedValue = selectedStandortId,
                onChange = { selectedStandortId = it },
                attrs = {
                    style {
                        width(320.px)
                    }
                }
            )


            LumaticaButton(
                label = if (editMode) "Vorschau" else "Edit",
                onClick = { editMode = !editMode },
                variant = LumaticaButtonVariant.Standard
            )


            if (editMode) {

                PaintBtn("Halle", paintType == HALLE) { paintType = HALLE }
                PaintBtn("Weg", paintType == WEG) { paintType = WEG }
                PaintBtn("Hof", paintType == HOF) { paintType = HOF }
                PaintBtn("Parken", paintType == PARKEN) { paintType = PARKEN }
                PaintBtn("Verwaltung", paintType == VERWALTUNG) { paintType = VERWALTUNG }
                PaintBtn("Papier", paintType == PAPIER) { paintType = PAPIER }
                PaintBtn("Toiletten", paintType == TOILETTE) { paintType = TOILETTE }
                PaintBtn("Kantine", paintType == KANTINE) { paintType = KANTINE }
                PaintBtn("Einfahrt", paintType == EINFAHRT) { paintType = EINFAHRT }
                PaintBtn("Ausfahrt", paintType == AUSFAHRT) { paintType = AUSFAHRT }
                PaintBtn("Tor", paintType == TOR) { paintType = TOR }
                PaintBtn("Parkplatz", paintType == PARKPLATZ) { paintType = PARKPLATZ }
                Span({
                    style {
                        color(DashboardTheme.TEXT_MUTED)
                        fontSize(14.px)
                        paddingLeft(4.px)
                    }
                }) {
                    Text("Tore im Layout: ${tore.size}/${echteTore.size}")
                }
                Span({
                    style {
                        color(DashboardTheme.TEXT_MUTED)
                        fontSize(14.px)
                        paddingLeft(4.px)
                    }
                }) {
                    Text("Parkplätze im Layout: ${parkplaetze.size}/${echteParkplaetze.size}")
                }

                /* ===== SAVE BUTTON ===== */
                LumaticaButton(
                    label = "Layout speichern",
                    onClick = onClick@{
                        val standortId = selectedStandortId ?: return@onClick

                        scope.launch {
                            YardApi.saveStandortLayout(
                                standortId,
                                All(
                                    standortTiles = tiles.mapKeys { "${it.key.first},${it.key.second}" },
                                    standortTore = tore.map {
                                        mapOf("id" to it.id, "x" to it.x, "y" to it.y)
                                    },
                                    standortParkplaetze = parkplaetze.map {
                                        mapOf("id" to it.id, "x" to it.x, "y" to it.y)
                                    },
                                    standortOutlines = outlines.map { o ->
                                        o.cells.map { "${it.first},${it.second}" }
                                    },
                                    standortLabels = labels.map { l ->
                                        mapOf(
                                            "id" to l.id.toString(),
                                            "name" to l.name,
                                            "type" to l.type,
                                            "reference" to l.reference,
                                            "cells" to l.cells.joinToString(";") {
                                                "${it.first},${it.second}"
                                            }
                                        )
                                    },
                                    standortZoom = zoom
                                )
                            )
                        }
                    },
                    variant = LumaticaButtonVariant.Standard,
                    enabled = selectedStandortId != null
                )


            }
        }

        /* ===== MAP VIEWPORT ===== */

        Div({
            style {
                position(Position.Relative)
                width(100.percent)
                height(90.vh)

                overflow("auto")
                property("user-select", "none")
            }

            onWheel {
                it.preventDefault()
                val delta = if (it.deltaY < 0) 0.1 else -0.1
                zoom = (zoom + delta).coerceIn(minzoom, maxzoom)
            }

            if (editMode) {
                onContextMenu { it.preventDefault() }

                onMouseUp {
                    isPainting = false

                    if (rightDown && dragStart != null && dragCurrent != null) {
                        val sel = buildRect(dragStart!!, dragCurrent!!)
                        selection = sel

                        if (sel.size == 1) {
                            val p = sel.first()
                            tiles.remove(p)
                            removeTor(p.first, p.second)
                            removeParkplatz(p.first, p.second)
                            labels.removeAll { p in it.cells }
                            outlines.removeAll { p in it.cells }
                            selection = emptySet()
                        } else {
                            labelMenuOpen = false
                            hallMenuOpen = false
                            menuVisible = true
                        }
                    }

                    rightDown = false
                    dragStart = null
                    dragCurrent = null
                }
            }
        }) {

            /* ===== ZOOM LAYER ===== */

            Div({
                style {
                    property("transform", "scale($zoom)")
                    property("transform-origin", "0 0")
                }
            }) {

                /* ===== GRID ===== */

                Div({
                    style {
                        display(DisplayStyle.Grid)
                        gridTemplateColumns("repeat($GRID_SIZE, ${TILE_SIZE}px)")
                    }
                }) {

                    for (y in 0 until GRID_SIZE) {
                        for (x in 0 until GRID_SIZE) {

                            val key = x to y
                            val type = tiles[key]

                            Div({
                                style {
                                    width(TILE_SIZE.px)
                                    height(TILE_SIZE.px)
                                    if (editMode) property("box-shadow", "inset 0 0 0 1px #444")
                                    backgroundColor(tileColor(type))
                                }

                                if (editMode) {

                                    onMouseDown {
                                        if (it.button.toInt() == 2) {
                                            it.preventDefault()
                                            rightDown = true
                                            dragStart = key
                                            dragCurrent = key
                                            menuPos = it.clientX to it.clientY
                                            return@onMouseDown
                                        }

                                        if (ghostActive && clipboard != null) {
                                            pasteBlock(clipboard!!, key, tiles)
                                            ghostActive = false
                                            ghostPos = null
                                            return@onMouseDown
                                        }

                                        isPainting = true
                                        if (paintType == TOR) addTor(x, y)
                                        else if (paintType == PARKPLATZ) addParkplatz(x, y)
                                        else tiles[key] = paintType
                                    }

                                    onMouseEnter {
                                        if (isPainting) {
                                            if (paintType == TOR) addTor(x, y)
                                            else if (paintType == PARKPLATZ) addParkplatz(x, y)
                                            else tiles[key] = paintType
                                        }
                                        if (rightDown) dragCurrent = key
                                        if (ghostActive) ghostPos = key
                                    }
                                }
                            })
                        }
                    }
                }

                /* ===== OUTLINES ===== */
                outlines.forEach { renderOutline(it) }

                /* ===== TORE ===== */
                tore.forEach { tor ->
                    val torZustand = echteTore.firstOrNull { (it.tor ?: 0) == tor.id }?.zustand
                    Div({
                        style {
                            position(Position.Absolute)
                            left((tor.x * TILE_SIZE).px)
                            top((tor.y * TILE_SIZE).px)
                            width(TILE_SIZE.px)
                            height(TILE_SIZE.px)

                            backgroundColor(Color(ZustandManager.colorHexFor(torZustand ?: ZustandManager.ANGELEGT)))

                            border(2.px, LineStyle.Solid, Color.black)
                            color(Color.white)
                            fontSize(10.px)
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            property("pointer-events", "none")
                            property("z-index", "60")
                        }
                    }) { Text("T${tor.id}") }
                }

                /* ===== PARKPLÄTZE ===== */
                parkplaetze.forEach { parkplatz ->
                    val parkplatzStamm = echteParkplaetze.firstOrNull { it.id.toIntOrNull() == parkplatz.id }
                    val zustand = when {
                        parkplatzStamm?.gesperrt == true -> ZustandManager.STORNIERT
                        parkplatzStamm?.belegt == true -> ZustandManager.AM_TOR
                        else -> ZustandManager.IN_VERLADUNG
                    }
                    Div({
                        style {
                            position(Position.Absolute)
                            left((parkplatz.x * TILE_SIZE).px)
                            top((parkplatz.y * TILE_SIZE).px)
                            width(TILE_SIZE.px)
                            height(TILE_SIZE.px)

                            backgroundColor(Color(ZustandManager.colorHexFor(zustand)))

                            border(2.px, LineStyle.Solid, Color.black)
                            color(Color.white)
                            fontSize(10.px)
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            property("pointer-events", "none")
                            property("z-index", "60")
                        }
                    }) { Text("P${parkplatz.id}") }
                }


                /* ===== LABELS ===== */
                labels.forEach { label ->
                    if (label.cells.isEmpty()) return@forEach
                    val cx = label.cells.map { it.first }.average()
                    val cy = label.cells.map { it.second }.average()

                    Div({
                        style {
                            position(Position.Absolute)
                            left((cx * TILE_SIZE).px)
                            top((cy * TILE_SIZE).px)
                            backgroundColor(Color("rgba(0,0,0,0.65)"))
                            color(Color.white)
                            padding(4.px)
                            borderRadius(4.px)
                            fontSize(12.px)
                            cursor("pointer")
                            property("z-index", "80")
                        }

                        onContextMenu {
                            it.preventDefault()
                            labels.remove(label)
                        }

                        onDoubleClick {
                            labelDialogEditId = label.id
                            labelDialogCells = label.cells
                            labelDialogType = label.type.ifBlank { LABEL_WERKSGELAENDE }
                            labelDialogHalle = label.reference
                            labelDialogOpen = true
                        }
                    }) {
                        Text(label.name)
                    }
                }

                if (editMode && dragStart != null && dragCurrent != null) {
                    SelectionOverlay(dragStart!!, dragCurrent!!)
                }

                if (editMode && ghostActive && clipboard != null && ghostPos != null) {
                    GhostOverlay(clipboard!!, ghostPos!!, ::tileColor)
                }
            }

            /* ===== CONTEXT MENU ===== */

            if (editMode && menuVisible) {
                Div({
                    style {
                        position(Position.Fixed)
                        left(menuPos.first.px)
                        top(menuPos.second.px)
                        property("background", DashboardTheme.PREMIUM_SURFACE_BG)
                        property("backdrop-filter", "blur(18px)")
                        property("border", "1px solid ${DashboardTheme.BORDER_LIGHT}")
                        borderRadius(12.px)
                        padding(8.px)
                        property("z-index", "999")
                        property("min-width", "180px")
                        property("box-shadow", "0 18px 40px ${DashboardTheme.SURFACE_SHADOW}")
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(6.px)
                    }
                }) {

                    MenuItem("Kopieren") {
                        clipboard = buildBlock(selection, tiles)
                        ghostActive = true
                        ghostPos = null
                        selection = emptySet()
                        labelMenuOpen = false
                        hallMenuOpen = false
                        menuVisible = false
                    }

                    MenuItem("Löschen") {

                        selection.forEach { cell ->
                            tiles.remove(cell)
                            removeTor(cell.first, cell.second)
                            removeParkplatz(cell.first, cell.second)
                        }

                        labels.removeAll { label ->
                            label.cells.any { it in selection }
                        }

                        outlines.removeAll { outline ->
                            outline.cells.any { it in selection }
                        }

                        selection = emptySet()
                        labelMenuOpen = false
                        hallMenuOpen = false
                        menuVisible = false
                    }

                    MenuItem(if (labelMenuOpen) "Label setzen ▲" else "Label setzen ▼") {
                        labelMenuOpen = !labelMenuOpen
                        hallMenuOpen = false
                    }

                    if (labelMenuOpen) {
                        val directLabelOptions = listOf(
                            LABEL_WERKSGELAENDE,
                            LABEL_PAPIERAUSGABE,
                            LABEL_PAPIERANNAHME,
                            LABEL_PARKBEREICH,
                            LABEL_TOILETTEN,
                            LABEL_KANTINE,
                            LABEL_EINFAHRT,
                            LABEL_AUSFAHRT
                        )
                        directLabelOptions.forEach { type ->
                            MenuItem("  ${areaLabelName(type)}") {
                                labels.add(
                                    AreaLabel(
                                        id = labelCounter++,
                                        name = areaLabelName(type),
                                        cells = selection.toSet(),
                                        type = type
                                    )
                                )
                                selection = emptySet()
                                labelMenuOpen = false
                                menuVisible = false
                            }
                        }

                        MenuItem(if (hallMenuOpen) "  Halle ▲" else "  Halle ▼") {
                            hallMenuOpen = !hallMenuOpen
                        }
                        if (hallMenuOpen) {
                            val selectedStandort = standorte.firstOrNull { it.id == selectedStandortId }
                            hallenIds(selectedStandort).forEach { hallId ->
                                MenuItem("    Halle $hallId") {
                                    labels.add(
                                        AreaLabel(
                                            id = labelCounter++,
                                            name = areaLabelName(LABEL_HALLE, hallId),
                                            cells = selection.toSet(),
                                            type = LABEL_HALLE,
                                            reference = hallId
                                        )
                                    )
                                    selection = emptySet()
                                    hallMenuOpen = false
                                    labelMenuOpen = false
                                    menuVisible = false
                                }
                            }
                        }
                    }


                    MenuItem("Umrandung setzen") {
                        outlines.add(Outline(selection))
                        selection = emptySet()
                        labelMenuOpen = false
                        hallMenuOpen = false
                        menuVisible = false
                    }

                    Hr()

                    MenuItem("Abbrechen") {
                        selection = emptySet()
                        hallMenuOpen = false
                        labelMenuOpen = false
                        menuVisible = false
                    }
                }
            }
        }
    }

    if (labelDialogOpen) {
        val selectedStandort = standorte.firstOrNull { it.id == selectedStandortId }
        val hallen = hallenIds(selectedStandort)
        val typeOptions = listOf(
            All(key = LABEL_WERKSGELAENDE, label = "Werksgelände"),
            All(key = LABEL_PAPIERAUSGABE, label = "Papierausgabe"),
            All(key = LABEL_PAPIERANNAHME, label = "Papierannahme"),
            All(key = LABEL_PARKBEREICH, label = "Parkbereich"),
            All(key = LABEL_HALLE, label = "Halle"),
            All(key = LABEL_TOILETTEN, label = "Toiletten"),
            All(key = LABEL_KANTINE, label = "Kantine"),
            All(key = LABEL_EINFAHRT, label = "Einfahrt"),
            All(key = LABEL_AUSFAHRT, label = "Ausfahrt")
        )
        PopupEngineCard(
            windowKey = "standort-layout-label",
            widthPx = 520,
            heightPx = if (labelDialogType == LABEL_HALLE) 330 else 270,
            minWidthPx = 460,
            minHeightPx = 250,
            viewportAnchored = true,
            onClose = { labelDialogOpen = false }
        ) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(16.px)
                    padding(24.px)
                    color(DashboardTheme.TEXT_LIGHT)
                }
            }) {
                H3({ style { margin(0.px) } }) {
                    Text(if (labelDialogEditId == null) "Label setzen" else "Label ändern")
                }
                Span({ style { color(DashboardTheme.TEXT_MUTED) } }) {
                    Text("Bezeichnung für den markierten Bereich auswählen.")
                }
                AppDropdown(
                    options = typeOptions,
                    selectedValue = labelDialogType,
                    onChange = {
                        labelDialogType = it
                        if (it != LABEL_HALLE) labelDialogHalle = ""
                    },
                    placeholder = "Bereich auswählen"
                )
                if (labelDialogType == LABEL_HALLE) {
                    AppDropdown(
                        options = hallen.map { All(key = it, label = "Halle $it") },
                        selectedValue = labelDialogHalle.takeIf { it.isNotBlank() },
                        onChange = { labelDialogHalle = it },
                        placeholder = "Halle auswählen",
                        enabled = hallen.isNotEmpty()
                    )
                }
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.FlexEnd)
                        gap(10.px)
                    }
                }) {
                    LumaticaButton(
                        label = "Abbrechen",
                        onClick = { labelDialogOpen = false }
                    )
                    val canSaveLabel = labelDialogType != LABEL_HALLE || labelDialogHalle.isNotBlank()
                    LumaticaButton(
                        label = "Übernehmen",
                        enabled = canSaveLabel,
                        variant = LumaticaButtonVariant.Action,
                        onClick = {
                            if (!canSaveLabel || labelDialogCells.isEmpty()) return@LumaticaButton
                            val label = AreaLabel(
                                id = labelDialogEditId ?: labelCounter++,
                                name = areaLabelName(labelDialogType, labelDialogHalle),
                                cells = labelDialogCells,
                                type = labelDialogType,
                                reference = labelDialogHalle
                            )
                            val existingIndex = labels.indexOfFirst { it.id == label.id }
                            if (existingIndex >= 0) labels[existingIndex] = label else labels.add(label)
                            labelDialogOpen = false
                        }
                    )
                }
            }
        }
    }
}

/* ================= HELPERS ================= */

private fun buildRect(a: Pair<Int, Int>, b: Pair<Int, Int>): Set<Pair<Int, Int>> {
    val minX = min(a.first, b.first)
    val maxX = max(a.first, b.first)
    val minY = min(a.second, b.second)
    val maxY = max(a.second, b.second)

    val set = mutableSetOf<Pair<Int, Int>>()
    for (y in minY..maxY)
        for (x in minX..maxX)
            set.add(x to y)
    return set
}

private fun buildBlock(
    selection: Set<Pair<Int, Int>>,
    tiles: Map<Pair<Int, Int>, String>
): Block {
    val minX = selection.minOf { it.first }
    val minY = selection.minOf { it.second }

    val rel = mutableMapOf<Pair<Int, Int>, String>()
    selection.forEach { abs ->
        tiles[abs]?.let {
            rel[(abs.first - minX) to (abs.second - minY)] = it
        }
    }
    return Block(rel)
}

private fun pasteBlock(
    block: Block,
    target: Pair<Int, Int>,
    tiles: MutableMap<Pair<Int, Int>, String>
) {
    block.tiles.forEach { (rel, t) ->
        tiles[(target.first + rel.first) to (target.second + rel.second)] = t
    }
}

/* ================= UI HELPERS ================= */

@Composable
private fun PaintBtn(
    label: String,
    active: Boolean = false,
    on: () -> Unit
) {
    LumaticaButton(
        label = label,
        onClick = { on() },
        variant = LumaticaButtonVariant.State,
        isActive = active
    )
}


@Composable
private fun MenuItem(label: String, onClick: () -> Unit) {
    var hovered by remember { mutableStateOf(false) }
    var pressed by remember { mutableStateOf(false) }

    Div({
        style {
            width(100.percent)
            property("box-sizing", "border-box")
            padding(0.px, 2.px)
            borderRadius(8.px)
            applyAppButtonChrome()
            if (hovered) {
                applyAppButtonHoverChrome()
                backgroundColor(Color("rgba(255, 255, 255, 0.05)"))
            }
            if (pressed) applyAppButtonActiveChrome()
        }
        onMouseEnter { hovered = true }
        onMouseLeave {
            hovered = false
            pressed = false
        }
        onMouseDown { pressed = true }
        onMouseUp { pressed = false }
        onClick {
            pressed = false
            onClick()
        }
    }) {
        Div({
            style {
                minHeight(36.px)
                width(100.percent)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                padding(0.px, 12.px)
                color(Color.white)
                property("box-sizing", "border-box")
                cursor("pointer")
            }
        }) {
            Text(label)
        }
    }
}

@Composable
private fun SelectionOverlay(a: Pair<Int, Int>, b: Pair<Int, Int>) {
    val minX = min(a.first, b.first)
    val maxX = max(a.first, b.first)
    val minY = min(a.second, b.second)
    val maxY = max(a.second, b.second)

    Div({
        style {
            position(Position.Absolute)
            left((minX * TILE_SIZE).px)
            top((minY * TILE_SIZE).px)
            width(((maxX - minX + 1) * TILE_SIZE).px)
            height(((maxY - minY + 1) * TILE_SIZE).px)
            backgroundColor(Color("rgba(0,120,255,0.18)"))
            border(2.px, LineStyle.Solid, Color("#007aff"))
            property("pointer-events", "none")
            property("z-index", "70")
        }
    })
}

@Composable
private fun GhostOverlay(
    block: Block,
    anchor: Pair<Int, Int>,
    tileColor: (String?) -> CSSColorValue
) {
    block.tiles.forEach { (rel, t) ->
        Div({
            style {
                position(Position.Absolute)
                left(((anchor.first + rel.first) * TILE_SIZE).px)
                top(((anchor.second + rel.second) * TILE_SIZE).px)
                width(TILE_SIZE.px)
                height(TILE_SIZE.px)
                backgroundColor(tileColor(t))
                property("opacity", "0.55")
                property("pointer-events", "none")
                property("box-shadow", "inset 0 0 0 1px rgba(0,0,0,0.25)")
                property("z-index", "65")
            }
        })
    }
}

@Composable
private fun renderOutline(outline: Outline) {
    val cells = outline.cells
    fun has(x: Int, y: Int) = cells.contains(x to y)

    cells.forEach { (x, y) ->
        if (!has(x, y - 1)) Edge(x, y, "top")
        if (!has(x, y + 1)) Edge(x, y, "bottom")
        if (!has(x - 1, y)) Edge(x, y, "left")
        if (!has(x + 1, y)) Edge(x, y, "right")
    }
}

@Composable
private fun Edge(x: Int, y: Int, side: String) {
    Div({
        style {
            position(Position.Absolute)
            left((x * TILE_SIZE).px)
            top((y * TILE_SIZE).px)
            width(TILE_SIZE.px)
            height(TILE_SIZE.px)
            property("pointer-events", "none")
            property("z-index", "50")

            when (side) {
                "top" -> property("border-top", "2px solid black")
                "bottom" -> property("border-bottom", "2px solid black")
                "left" -> property("border-left", "2px solid black")
                "right" -> property("border-right", "2px solid black")
            }
        }
    })
}
